/**
 * ConvergenceCellInspector — cycle-102 sprint-1 Task 1.3.
 *
 * Inspector list for convergence cells with ≥3 co-located source groups
 * (PRD §5.7.2). Cells are tagged with the iris accent so the operator
 * can scan candidates quickly; clicking a cell expands a sidebar with
 * the per-cell breakdown (sources, theatres, density, signal count —
 * truncated at 50 per PRD §5.7.2).
 *
 * Frontend-side adapter: the existing `/convergence/heatmap` payload
 * does not yet emit `h3_cell` or `source_groups` directly (sprint plan
 * Task 1.3.c — adapt at boundary; no breaking backend change). The
 * adapter lives in `intelClient.ts::adaptConvergenceCells`.
 *
 * Out-of-scope for sprint-1: 3D iris accent overlay on `GlobeCanvas`
 * cell positions — deferred until GlobeCanvas exposes per-cell
 * addressing (cycle-102.5 follow-on noted in reviewer.md).
 */

import { useMemo, useState } from 'react';

import { cn } from '../../../lib/utils';
import {
  CONVERGENCE_THRESHOLD_SOURCE_GROUPS,
  adaptConvergenceCells,
  type ConvergenceCellView,
  type ConvergenceHeatmapResponse,
} from '../../../api/intelClient';

const MAX_SIGNALS_PER_CELL = 50;

interface ConvergenceCellInspectorProps {
  data: ConvergenceHeatmapResponse | undefined;
  isLoading: boolean;
  error: unknown;
  className?: string;
}

export function ConvergenceCellInspector({
  data,
  isLoading,
  error,
  className,
}: ConvergenceCellInspectorProps) {
  const cells = useMemo(() => (data ? adaptConvergenceCells(data) : []), [data]);
  const converged = useMemo(
    () => cells.filter((c) => c.isConverged),
    [cells],
  );
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const active = useMemo(
    () => converged.find((c) => c.cellKey === activeKey) ?? null,
    [converged, activeKey],
  );

  return (
    <section
      aria-label="Convergence cell inspector"
      className={cn(
        'flex h-full overflow-hidden rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-card)]/85 backdrop-blur-md',
        className,
      )}
    >
      <div className="flex flex-col w-[280px] min-w-[220px] border-r border-[var(--e-border-secondary)]/60">
        <header className="px-3 py-2 border-b border-[var(--e-border-secondary)]/60">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
            Convergence cells
          </span>
          <div className="mt-0.5 text-[11px] font-mono text-[var(--e-text-secondary)]">
            ≥{CONVERGENCE_THRESHOLD_SOURCE_GROUPS} source groups
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          {isLoading && !data && (
            <p
              role="status"
              aria-live="polite"
              className="px-3 py-2 text-[10px] font-mono text-[var(--e-text-muted)]"
            >
              Loading convergence…
            </p>
          )}
          {Boolean(error) && !data && (
            <p
              role="alert"
              data-testid="convergence-error"
              className="px-3 py-2 text-[10px] font-mono text-amber-400"
            >
              Convergence unavailable
            </p>
          )}
          {data && converged.length === 0 && (
            <p
              role="status"
              className="px-3 py-2 text-[10px] font-mono text-[var(--e-text-muted)]"
            >
              No converged cells.
            </p>
          )}
          <ul role="list" className="flex flex-col">
            {converged.map((cell) => {
              const selected = cell.cellKey === activeKey;
              return (
                <li key={cell.cellKey}>
                  <button
                    type="button"
                    onClick={() => setActiveKey(cell.cellKey)}
                    aria-pressed={selected}
                    data-testid={`convergence-row-${cell.cellKey}`}
                    className={cn(
                      'w-full text-left px-3 py-2 border-l-2 transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--e-purple-400)]',
                      selected
                        ? 'border-l-[var(--e-purple-400)] bg-[var(--e-purple-400)]/8'
                        : 'border-l-transparent hover:bg-[var(--e-bg-elevated)]/40',
                    )}
                  >
                    <div className="flex items-center justify-between text-[10px] font-mono text-[var(--e-text-muted)]">
                      <span>
                        {cell.lat.toFixed(2)}, {cell.lon.toFixed(2)}
                      </span>
                      <span
                        data-testid={`convergence-iris-${cell.cellKey}`}
                        className="inline-block w-2 h-2 rounded-full bg-[var(--e-purple-400)]"
                        aria-label="Converged cell — iris accent"
                      />
                    </div>
                    <div className="mt-1 text-[11px] text-[var(--e-text-secondary)]">
                      {cell.sourceGroups.length} groups · {cell.signals} signals
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {active ? (
          <CellDetail cell={active} />
        ) : (
          <p
            role="status"
            className="text-[11px] font-mono text-[var(--e-text-muted)]"
          >
            Select a converged cell to expand.
          </p>
        )}
      </div>
    </section>
  );
}

function CellDetail({ cell }: { cell: ConvergenceCellView }) {
  const truncated = cell.signals > MAX_SIGNALS_PER_CELL;
  const shownSignals = Math.min(cell.signals, MAX_SIGNALS_PER_CELL);

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <h3 className="text-[12px] font-mono uppercase tracking-wider text-[var(--e-text-primary)]">
          Cell · {cell.lat.toFixed(3)}, {cell.lon.toFixed(3)}
        </h3>
        <span className="text-[10px] font-mono text-[var(--e-text-muted)]">
          density {cell.density.toFixed(2)}
        </span>
      </header>

      <DetailBlock title="Source groups">
        <div className="flex flex-wrap gap-1">
          {cell.sourceGroups.map((g) => (
            <span
              key={g}
              data-testid={`source-group-${g}`}
              className="px-1.5 py-0.5 rounded-sm border border-[var(--e-purple-400)]/40 text-[10px] font-mono text-[var(--e-text-secondary)] bg-[var(--e-purple-400)]/8"
            >
              {g}
            </span>
          ))}
        </div>
      </DetailBlock>

      {cell.theatres.length > 0 && (
        <DetailBlock title="Theatres">
          <div className="flex flex-wrap gap-1">
            {cell.theatres.map((t) => (
              <span
                key={t}
                className="px-1.5 py-0.5 rounded-sm border border-[var(--e-border-secondary)] text-[10px] font-mono text-[var(--e-text-secondary)]"
              >
                {t}
              </span>
            ))}
          </div>
        </DetailBlock>
      )}

      <DetailBlock
        title={`Contributing signals (${shownSignals}${truncated ? ` of ${cell.signals}` : ''})`}
      >
        <p className="text-[11px] font-mono text-[var(--e-text-secondary)]">
          {shownSignals} signals contributed to this cell.
          {truncated && (
            <span data-testid="convergence-truncated" className="block mt-1 text-[var(--e-text-muted)]">
              Truncated at {MAX_SIGNALS_PER_CELL} per PRD §5.7.2.
            </span>
          )}
        </p>
      </DetailBlock>
    </div>
  );
}

function DetailBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1.5">
      <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
        {title}
      </span>
      {children}
    </section>
  );
}
